import {type BoardCell, type BoardData, boardCellKey, type CellAddress, cellNeedsFirstPage} from '@github-ui/board-core'

import type {BoardCellCursors} from './types'

/**
 * Where a cell's next request starts, or `null` when the cell has nothing to ask for.
 *
 * A first page is asked for without a cursor when `itemsLoaded` is false and the count is
 * positive or unknown. An explicitly empty cell, and a cell from a server that does not state
 * `itemsLoaded`, have no first page to fetch. Any other cell resumes from the server's cursor.
 */
export function cellPageCursor(
  cell: BoardCell<unknown> | undefined,
  cursors: BoardCellCursors | undefined,
): {cursor: string | null} | null {
  if (cell === undefined) return null
  if (needsFirstPage(cell)) return {cursor: null}
  if (cell.itemsLoaded === false || !cell.hasMoreItems) return null

  const cursor = cursors?.get(boardCellKey(cell))
  return cursor === undefined ? null : {cursor}
}

/**
 * The cells whose first page is worth reading before the reader reaches them: those within
 * `lead.columns` columns and `lead.rows` rows of the renderer's window, in the order to ask for them.
 *
 * The window already reaches one item past the screen on each side, so a cell inside it comes
 * before any in the lead. Within either, nearer the window comes first, then board order.
 */
export function cellsToPreload<TItem>(
  data: BoardData<TItem>,
  {columnKeys, rowKeys}: {columnKeys: readonly string[]; rowKeys: readonly string[]},
  lead: {columns: number; rows: number},
): CellAddress[] {
  const columnSpan = windowSpan(data.columns, columnKeys)
  const rowSpan = windowSpan(data.rows, rowKeys)
  if (columnSpan === null || rowSpan === null) return []

  const candidates: Array<{address: CellAddress; distance: number; row: number; column: number}> = []
  for (const cell of data.cells) {
    if (!needsFirstPage(cell)) continue

    const column = columnSpan.indexes.get(cell.columnKey)
    const row = rowSpan.indexes.get(cell.rowKey)
    if (column === undefined || row === undefined) continue

    const columnsOut = columnSpan.outside(column)
    const rowsOut = rowSpan.outside(row)
    if (columnsOut > lead.columns || rowsOut > lead.rows) continue

    const distance = Math.max(columnsOut, rowsOut)
    candidates.push({address: {columnKey: cell.columnKey, rowKey: cell.rowKey}, distance, row, column})
  }

  candidates.sort((a, b) => a.distance - b.distance || a.row - b.row || a.column - b.column)
  return candidates.map(candidate => candidate.address)
}

function needsFirstPage(cell: BoardCell<unknown>): boolean {
  // Live refreshes can create a cell omitted from a truncated grid without a server count.
  // The shared helper only handles counted cells, so keep unknown first pages eligible here.
  return cellNeedsFirstPage(cell) || (cell.itemsLoaded === false && cell.totalCount === undefined)
}

type WindowSpan = {
  indexes: ReadonlyMap<string, number>
  /** How many items past the window an index is, and 0 inside it. */
  outside: (index: number) => number
}

function windowSpan(values: ReadonlyArray<{key: string}>, windowKeys: readonly string[]): WindowSpan | null {
  const indexes = new Map(values.map((value, index) => [value.key, index]))

  let first = Infinity
  let last = -Infinity
  for (const key of windowKeys) {
    const index = indexes.get(key)
    if (index === undefined) continue
    first = Math.min(first, index)
    last = Math.max(last, index)
  }
  if (first > last) return null

  return {
    indexes,
    outside: index => (index < first ? first - index : index > last ? index - last : 0),
  }
}
